// Bridges the pipeline engine onto the prototype's existing mock dataset.
// The POC shipped its own tiny ROWS/FIELDS; here we derive the same shapes from
// MOCK_DEALS so a pipeline and a ViewConfig query the identical source rows.
import { MOCK_DEALS } from './mock-data'
import { Cell, FieldSchema, Row } from './pipeline-types'
import { DealRow } from './view-config'

/** ISO date/datetime columns on DealRow — drive date bucketing in groupby. */
export const DATEF: string[] = [
  'created',
  'actual_close_date',
  'projected_close_date',
]

/** Numeric DealRow columns offered as aggregate measures in the op forms. */
export const NUMF: string[] = [
  'monthly_volume',
  'annual_volume',
  'monthly_revenue',
  'annual_revenue',
  'forecasted_monthly_volume',
  'forecasted_annual_revenue',
  'win_probability',
  'days_in_stage',
  'days_since_update',
]

/** Categorical columns offered as groupby dimensions (plus a date option). */
export const CAT_DIMS: string[] = [
  'sales_stage',
  'account_owner',
  'category',
  'sub_category',
  'product',
]

/** Array-valued columns can't be filtered/grouped scalarly; hide from the schema. */
const SKIP_FIELDS: (keyof DealRow)[] = ['distributor_names', 'gpo_names']

/**
 * Flatten records into plain pipeline Rows (scalar columns only) and derive the
 * matching field schema. Shared by every frame so deals and deal_activity infer
 * column types identically: `dateFields` are typed `date`, numbers `num`, the
 * rest `cat` (carrying their distinct values). Array/null columns are dropped.
 */
export function materialize(
  records: Record<string, unknown>[],
  dateFields: string[],
  skipFields: string[] = []
): { rows: Row[]; fields: FieldSchema[] } {
  const rows: Row[] = records.map((rec) => {
    const row: Row = {}
    Object.keys(rec).forEach((key) => {
      if (skipFields.includes(key)) return
      const v = rec[key]
      if (v === null || v === undefined) return
      if (typeof v === 'number' || typeof v === 'string') row[key] = v
    })
    return row
  })

  const names = new Set<string>()
  rows.forEach((r) => Object.keys(r).forEach((k) => names.add(k)))
  const fields: FieldSchema[] = [...names].map((name) => {
    const sample = rows.find((r) => r[name] != null)?.[name]
    const type: FieldSchema['type'] = dateFields.includes(name)
      ? 'date'
      : typeof sample === 'number'
        ? 'num'
        : 'cat'
    const field: FieldSchema = { name, type }
    if (type === 'cat') {
      field.values = [
        ...new Set(
          rows.map((r) => r[name]).filter((x): x is Cell => x != null)
        ),
      ]
    }
    return field
  })
  return { rows, fields }
}

const deal = materialize(MOCK_DEALS, DATEF, SKIP_FIELDS as string[])

/** Materialized deal rows (scalar columns only) — the default frame source. */
export const ROWS: Row[] = deal.rows

/** Field schema for the deal frame; categorical cols carry distinct values. */
export const FIELDS: FieldSchema[] = deal.fields
